import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { validateFormData } from 'cng-common/features/form/helpers/validateFormData'
import { Button, IButtonProps } from 'core-components/library/button'
import { Loader, LoaderOverlay } from 'core-components/library/loader'
import { Tooltip } from 'core-components/library/tooltip'
import { Controller } from 'core-components/library/validation'
import {
  IAudienceCondition,
  VisualAudienceExtended,
} from 'core/components/shared/ui/VisualAudienceExtended'
import { IRequestorLA } from 'onboarding/classes/models/requestorLA'
import { fail, success } from 'onboarding/classes/services/notification'

import { REQUESTOR_TO_FILL_LA_T1_FORM_KEY } from '../configs'

import { FormFields } from './FormFields'
import { getInitialFormData } from './helpers/getInitialFormData'
import { mapToDB } from './helpers/mapToDB'
import { mapToForm } from './helpers/mapToForm'
import { useRequestorFillLAData } from './hooks/useRequestorFillLAData'
import { TRequestorToFillLAT1Form } from './models'

import styles from './RequestorFillLAT1.module.scss'

interface IProps {
  requestId: number
  audienceSettings: IAudienceCondition
  disabled?: boolean
}

export const RequestorFillLAT1 = memo(({ requestId, audienceSettings, disabled }: IProps) => {
  const dispatch = useAppDispatch()
  const [defaultFormData, setDefaultFormData] = useState(getInitialFormData)
  const [fetchedRequestorLA, setFetchedRequestorLA] = useState<IRequestorLA | null>(null)

  const { requestorLAWip, requestWip, isSaving, cruds, onSave, partnerTerritory } =
    useRequestorFillLAData(
      setFetchedRequestorLA,
      requestId,
      fetchedRequestorLA?.HPContractAdminLocation ?? '',
      fetchedRequestorLA?.BuyerType ?? ''
    )
  const [{ formData, modifiedFields }] = useGetFormData<TRequestorToFillLAT1Form>(
    REQUESTOR_TO_FILL_LA_T1_FORM_KEY
  )

  const isFormChanged = useMemo(() => Boolean(modifiedFields.length), [modifiedFields])

  const onHandleSave = useCallback(async () => {
    const errors = await validateFormData(formData, REQUESTOR_TO_FILL_LA_T1_FORM_KEY)
    if (!errors.length && isFormChanged) {
      const result = await onSave(mapToDB(formData))
      if (result) {
        setDefaultFormData(mapToForm(result))
        success('Saved')
      } else {
        fail('Failed to save')
      }
    }
  }, [isFormChanged, onSave, formData])

  useEffect(() => {
    if (fetchedRequestorLA) {
      setDefaultFormData(mapToForm(fetchedRequestorLA))
    }
  }, [fetchedRequestorLA])

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(REQUESTOR_TO_FILL_LA_T1_FORM_KEY))
    }
  }, [])

  return requestorLAWip || requestWip ? (
    <Loader />
  ) : (
    <Controller>
      <div className={styles.container}>
        <LoaderOverlay show={isSaving} />
        <FormFields
          formKey={REQUESTOR_TO_FILL_LA_T1_FORM_KEY}
          partnerTerritory={partnerTerritory}
          hpContractAdminLocation={defaultFormData.HPContractAdminLocation}
          partnerTerritoryCodes={defaultFormData.PartnerTerritoryISO}
          buyerType={defaultFormData.BuyerType}
          specialPrograms={defaultFormData.SpecialPrograms}
          exhibits={defaultFormData.Exhibits}
          additionalComments={defaultFormData.AdditionalComments}
          writeAudience={audienceSettings}
          cruds={cruds}
          isDisabled={disabled}
        />
      </div>

      <div className={styles.btnWrapper}>
        <Tooltip
          tipContent={() => <span>No changes to save</span>}
          disabled={isFormChanged || disabled}
        >
          <VisualAudienceExtended<IButtonProps>
            write={audienceSettings}
            component={Button}
            onClick={onHandleSave}
            disabled={!isFormChanged || disabled}
            loading={isSaving}
          >
            Save
          </VisualAudienceExtended>
        </Tooltip>
      </div>
    </Controller>
  )
})
